import React, { useState } from "react";
import { ACHIEVEMENTS, TIER_STYLE } from "../data/achievementData";

const FILTERS = ["All", "Bronze", "Silver", "Gold"];

const BadgeTile = ({ ach, unlocked, selected, onClick }) => {
  const t = TIER_STYLE[ach.tier];
  if (!unlocked) {
    return (
      <button
        onClick={onClick}
        className="rounded-2xl border-2 border-gray-700 bg-gray-800 p-3 flex flex-col items-center gap-1.5 w-full text-left"
        style={{ boxShadow: selected ? "0 0 10px rgba(156,163,175,0.3)" : undefined, opacity: selected ? 1 : 0.55 }}
      >
        <div className="text-2xl grayscale">🔒</div>
        <div className="text-xs font-semibold text-gray-400 text-center leading-tight">{ach.name}</div>
        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-gray-700 text-gray-400">
          {t.label}
        </span>
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      className="rounded-2xl border-2 p-3 flex flex-col items-center gap-1.5 transition-all text-left w-full"
      style={{
        borderColor: selected ? t.border : t.border,
        background: selected ? t.bg : "#ffffff",
        boxShadow: selected ? `0 0 14px ${t.glow}` : "0 1px 3px rgba(0,0,0,0.1)",
        transform: selected ? "scale(1.04)" : undefined,
      }}
    >
      <div className="text-3xl">{ach.emoji}</div>
      <div className="text-xs font-bold text-center text-gray-800 leading-tight">{ach.name}</div>
      <span
        className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
        style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}` }}
      >
        {t.label}
      </span>
    </button>
  );
};

const Achievements = ({ achievements = [], onClose }) => {
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  const earned = new Set(achievements);
  const unlockedCount = ACHIEVEMENTS.filter(a => earned.has(a.id)).length;
  const total = ACHIEVEMENTS.length;

  const visible = ACHIEVEMENTS.filter(a =>
    filter === "All" || a.tier === filter.toLowerCase()
  );

  const handleSelect = (ach) => {
    setSelected(selected?.id === ach.id ? null : ach);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <button onClick={onClose} className="text-sm text-gray-300 hover:text-white">← Back</button>
        <span className="font-bold text-white text-sm">🏅 Achievements</span>
        <span className="text-xs text-gray-400">{unlockedCount}/{total}</span>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-3 pb-2 bg-gray-900 flex-shrink-0">
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${total > 0 ? (unlockedCount / total) * 100 : 0}%`,
              background: "linear-gradient(to right, #f59e0b, #eab308)",
            }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-1.5 text-center">
          {unlockedCount === 0 && "Win battles to earn achievements"}
          {unlockedCount > 0 && unlockedCount < total && `${unlockedCount} of ${total} badges unlocked`}
          {unlockedCount === total && "🎉 All achievements unlocked!"}
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1.5 px-4 py-2 bg-gray-900 flex-shrink-0 overflow-x-auto">
        {FILTERS.map(f => {
          const active = filter === f;
          const t = f !== "All" ? TIER_STYLE[f.toLowerCase()] : null;
          return (
            <button
              key={f}
              onClick={() => { setFilter(f); setSelected(null); }}
              className="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0"
              style={
                active && t
                  ? { background: t.bg, color: t.color, border: `1.5px solid ${t.border}` }
                  : active
                  ? { background: "#374151", color: "#f9fafb", border: "1.5px solid #6b7280" }
                  : { background: "#1f2937", color: "#9ca3af", border: "1.5px solid #374151" }
              }
            >
              {f}
            </button>
          );
        })}
      </div>

      {/* Badge grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {visible.map(ach => (
            <BadgeTile
              key={ach.id}
              ach={ach}
              unlocked={earned.has(ach.id)}
              selected={selected?.id === ach.id}
              onClick={() => handleSelect(ach)}
            />
          ))}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (() => {
        const t = TIER_STYLE[selected.tier];
        const isUnlocked = earned.has(selected.id);
        return (
          <div
            className="flex-shrink-0 border-t-2 p-4"
            style={isUnlocked
              ? { background: t.bg, borderColor: t.border }
              : { background: "#1f2937", borderColor: "#374151" }
            }
          >
            <div className="flex gap-4 items-start">
              <div className={`text-5xl flex-shrink-0 ${isUnlocked ? "" : "grayscale opacity-50"}`}>
                {isUnlocked ? selected.emoji : "🔒"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`font-extrabold text-base ${isUnlocked ? "text-gray-900" : "text-gray-300"}`}>
                    {selected.name}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={isUnlocked
                      ? { background: t.glow, color: t.color }
                      : { background: "#374151", color: "#9ca3af" }
                    }
                  >
                    {t.label}
                  </span>
                </div>
                {isUnlocked
                  ? <p className="text-sm text-gray-700 leading-relaxed mt-1">{selected.description}</p>
                  : <>
                      <p className="text-xs text-gray-400 mt-0.5 mb-1">🔒 Locked</p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        <span className="font-semibold text-gray-200">How to unlock: </span>
                        {selected.description}
                      </p>
                    </>
                }
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-500 hover:text-gray-300 flex-shrink-0 text-lg leading-none"
              >✕</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Achievements;
